import React, {
	type MutableRefObject,
	memo,
	useMemo,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
} from 'react';
import throttle from 'lodash/throttle';
import { useResizeObserver } from '@atlassian/jira-business-resize-observer';
import {
	columnWithMarginWidth,
	collapsedColumnWithMarginWidth,
} from '@atlassian/jira-platform-board-kit/src/common/constants/styles';
import type { BoardColumn as BoardColumnType } from '../../../common/types';
import { useVirtualItems } from '../../../controllers/virtualizer-old';
import { DraggingPlaceholder, VirtualColumnsContainer } from './styled';

type ColumnsPropsShared = {
	columnRenderer: (arg1: {
		columnIndex: number;
		column: BoardColumnType;
		boardSize: {
			width: number;
			height: number;
		};
		isVirtualized: boolean;
	}) => ReactNode;
	columns: BoardColumnType[];
	draggingColumnIndex?: number;
	columnsHeight?: number;
	columnsRef?: MutableRefObject<HTMLDivElement | null>;
	scrollRef: {
		current: HTMLElement | null;
	};
};

type ColumnsProps = ColumnsPropsShared & {
	scrollElement: HTMLElement;
};

const VirtualColumns = ({
	scrollRef,
	scrollElement,
	columns,
	columnRenderer,
	draggingColumnIndex,
	columnsHeight,
	columnsRef,
}: ColumnsProps) => {
	const getColumnWidth = useCallback(
		(columnIndex: number) => {
			const isColumnCollapsed = columnIndex != null && !!columns[columnIndex].isCollapsed;
			if (isColumnCollapsed) return collapsedColumnWithMarginWidth;
			return columnWithMarginWidth;
		},
		[columns],
	);

	const [boardSize, setBoardSize] = useState({
		width: scrollElement.offsetWidth,
		height: scrollElement.offsetHeight,
	});

	const useObserver = useCallback(() => boardSize, [boardSize]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const resizeObserverCallback = useCallback(
		throttle(() => {
			setBoardSize({
				width: scrollElement.offsetWidth,
				height: scrollElement.offsetHeight,
			});
		}, 100),
		[scrollElement],
	);

	// update board dimensions on browser resize
	useResizeObserver(scrollElement, resizeObserverCallback);

	const { virtualItems, totalSize } = useVirtualItems({
		horizontal: true,
		scrollRef,
		size: columns.length,
		estimateSize: getColumnWidth,
		useObserver,
	});

	const virtualItemsIndexes = virtualItems.map((item) => item.index);

	// We can't dynamically add/remove Droppables during a drag on scroll, see https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/changes-while-dragging.md#rules
	// That's why all columns are rendered all the time no matter the scroll position, but "virtualized" columns have no cards.
	// That way we won't be dynamically adding Droppables, which fixes crads drag-n-drop.
	const virtualColumns = useMemo(
		() =>
			columns.map((column, columnIndex) => ({
				column,
				columnIndex,
				boardSize,
				isVirtualized: !virtualItemsIndexes.includes(columnIndex),
			})),
		[columns, boardSize, virtualItemsIndexes],
	);

	const renderedColumns = virtualColumns.map((item) => columnRenderer(item));

	// Adding a placeholder node here needed to insert space for the lifted item when using renderClone.
	// Normaly you'd "position: absolute" all items and wouldn't need placeholder,
	// but in case of columns it is hard to deal with position:absolute as parent height collapses,
	// which forces us to calculate columns height with JS on page load.
	// So instead, this dynamically inserted placeholder doesn't have TTI impact.
	if (draggingColumnIndex != null && draggingColumnIndex > -1) {
		renderedColumns.splice(
			draggingColumnIndex,
			0,
			<DraggingPlaceholder
				key={columns[draggingColumnIndex].id}
				width={getColumnWidth(draggingColumnIndex)}
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="work-management-board.ui.board.virtual-columns.dragging-placeholder"
			/>,
		);
	}

	return (
		<VirtualColumnsContainer
			ref={columnsRef}
			width={totalSize}
			// @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'string | number'.
			height={columnsHeight}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="work-management-board.ui.board.virtual-columns.virtual-columns-container"
		>
			{renderedColumns}
		</VirtualColumnsContainer>
	);
};

export default memo<ColumnsPropsShared>(({ scrollRef, ...rest }: ColumnsPropsShared) => {
	const [scrollElement, setScrollElement] = useState(scrollRef.current);

	// make sure to render the columns when scrollRef is set
	useEffect(() => {
		if (scrollRef.current) {
			setScrollElement(scrollRef.current);
		}
	}, [scrollElement, scrollRef]);

	// react-virtual sometimes renders blank page if we don't have this check
	if (!scrollElement) {
		return null;
	}

	return <VirtualColumns scrollRef={scrollRef} scrollElement={scrollElement} {...rest} />;
});
